import type { Enums } from '@dolfin/business/db_types'

export type SourceColumnDefinition = {
  type: Enums<'custom_field_type'>
}

type SourceColumnTypes = Record<string, Enums<'custom_field_type'>>

const DEAL_COLUMN_TYPES: SourceColumnTypes = {
  deal_id: 'text',
  amount: 'number',
  close_date: 'date',
  stage: 'text',
  type: 'text',
  owner_id: 'user',
  name: 'text',
  crm_provider: 'text',
  created_at: 'date',
  pending_processing: 'boolean',
  has_invoices: 'boolean'
}

const INVOICE_COLUMN_TYPES: SourceColumnTypes = {
  invoice_number: 'text',
  deal_link_id: 'text',
  deal_name: 'text',
  deal_id: 'text',
  status: 'text',
  currency: 'text',
  due_date: 'date',
  effective_at: 'date',
  total: 'number',
  total_excluding_tax: 'number',
  sub_total: 'number',
  amount_adjusted: 'number',
  lines: 'text',
  payroll_provider: 'text',
  paid: 'boolean',
  payment_date: 'date',
  issued_credit_notes: 'number',
  issued_credit_notes_without_taxes: 'number',
  subscription_id: 'text',
  subscription_start_date: 'date',
  deal_invoice_order: 'number',
  created_at: 'date'
}

const KPI_COLUMN_TYPES: SourceColumnTypes = {
  id: 'number',
  period_start: 'date',
  profile_id: 'user',
  created_at: 'date'
}

const CUSTOM_RECORD_COLUMN_TYPES: SourceColumnTypes = {
  record_id: 'text',
  owner_id: 'user',
  external_owner_id: 'text',
  provider: 'text',
  created_at: 'date',
  updated_at: 'date'
}

const ACTIVITY_COLUMN_TYPES: SourceColumnTypes = {
  activity_id: 'text',
  activity_date: 'date',
  status: 'text',
  type: 'text',
  owner_id: 'user',
  crm_provider: 'text',
  created_at: 'date'
}

const LINE_ITEM_COLUMN_TYPES: SourceColumnTypes = {
  description: 'text',
  status: 'text',
  trial_days: 'number',
  is_featured: 'boolean',
  amount: 'number',
  unit_amount: 'number',
  quantity: 'number',
  tax_rate: 'number',
  tax_amount: 'text',
  currency: 'text',
  interval: 'text',
  interval_count: 'number',
  product_id: 'text',
  product_name: 'text'
}

const DEAL_LINE_ITEM_COLUMN_TYPES: SourceColumnTypes = {
  line_item_id: 'text',
  product_id: 'text',
  product_name: 'text',
  description: 'text',
  sku: 'text',
  quantity: 'number',
  unit_amount: 'number',
  amount: 'number',
  discount_amount: 'number',
  list_unit_amount: 'number',
  currency: 'text',
  pricebook_entry_id: 'text',
  service_date: 'date',
  billing_start_date: 'date',
  billing_end_date: 'date',
  billing_frequency: 'text',
  billing_term: 'text',
  crm_provider: 'text'
}

const prefixColumnTypes = (
  prefix: string,
  columns: SourceColumnTypes
): SourceColumnTypes =>
  Object.fromEntries(
    Object.entries(columns).map(([name, type]) => [`${prefix}${name}`, type])
  )

const SOURCE_COLUMN_TYPES: Record<Enums<'plan_source'>, SourceColumnTypes> = {
  deal: DEAL_COLUMN_TYPES,
  invoice: {
    ...INVOICE_COLUMN_TYPES,
    ...prefixColumnTypes('deal.', DEAL_COLUMN_TYPES)
  },
  kpi: KPI_COLUMN_TYPES,
  custom_record: CUSTOM_RECORD_COLUMN_TYPES,
  activity: {
    ...ACTIVITY_COLUMN_TYPES,
    ...prefixColumnTypes('deal.', DEAL_COLUMN_TYPES)
  },
  line_item: {
    ...LINE_ITEM_COLUMN_TYPES,
    ...prefixColumnTypes('invoice.', INVOICE_COLUMN_TYPES),
    ...prefixColumnTypes('deal.', DEAL_COLUMN_TYPES)
  },
  deal_line_item: {
    ...DEAL_LINE_ITEM_COLUMN_TYPES,
    ...prefixColumnTypes('deal.', DEAL_COLUMN_TYPES)
  }
}

const toSourceColumnDefinitions = (
  columns: SourceColumnTypes
): Record<string, SourceColumnDefinition> =>
  Object.fromEntries(
    Object.entries(columns).map(([name, type]) => [name, { type }])
  )

const SOURCE_COLUMNS = Object.fromEntries(
  Object.entries(SOURCE_COLUMN_TYPES).map(([source, columns]) => [
    source,
    toSourceColumnDefinitions(columns)
  ])
) as Record<Enums<'plan_source'>, Record<string, SourceColumnDefinition>>

export function getSourceColumns(
  source: Enums<'plan_source'> | undefined
): Record<string, SourceColumnDefinition> {
  return source ? SOURCE_COLUMNS[source] : {}
}
